import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

export default function ManualEntry({ clients, onSave, API }) {
  const [metricsForm, setMetricsForm] = useState({
    client_id: "",
    platform: "facebook",
    date: new Date().toISOString().split("T")[0],
    impressions: "",
    clicks: "",
    ctr: "",
    spend: "",
    likes: "",
    comments: "",
    shares: "",
    new_followers: "",
  });

  const [adForm, setAdForm] = useState({
    client_id: "",
    platform: "facebook",
    ad_name: "",
    creative_type: "image",
    date: new Date().toISOString().split("T")[0],
    impressions: "",
    clicks: "",
    ctr: "",
    spend: "",
    conversions: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleMetricsChange = (field, value) => {
    setMetricsForm((prev) => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate CTR
      if ((field === "impressions" || field === "clicks") && updated.impressions && updated.clicks) {
        const impressions = parseFloat(updated.impressions) || 0;
        const clicks = parseFloat(updated.clicks) || 0;
        if (impressions > 0) {
          updated.ctr = ((clicks / impressions) * 100).toFixed(2);
        }
      }
      
      return updated;
    });
  };

  const handleAdChange = (field, value) => {
    setAdForm((prev) => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate CTR
      if ((field === "impressions" || field === "clicks") && updated.impressions && updated.clicks) {
        const impressions = parseFloat(updated.impressions) || 0;
        const clicks = parseFloat(updated.clicks) || 0;
        if (impressions > 0) {
          updated.ctr = ((clicks / impressions) * 100).toFixed(2);
        }
      }
      
      return updated;
    });
  };

  const submitMetrics = async (e) => {
    e.preventDefault();
    if (!metricsForm.client_id) {
      toast.error("Please select a client");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/metrics`, {
        ...metricsForm,
        impressions: parseInt(metricsForm.impressions) || 0,
        clicks: parseInt(metricsForm.clicks) || 0,
        ctr: parseFloat(metricsForm.ctr) || 0,
        spend: parseFloat(metricsForm.spend) || 0,
        likes: parseInt(metricsForm.likes) || 0,
        comments: parseInt(metricsForm.comments) || 0,
        shares: parseInt(metricsForm.shares) || 0,
        new_followers: parseInt(metricsForm.new_followers) || 0,
      });
      toast.success("Metrics saved successfully!");
      onSave();
      // Reset form but keep client and platform
      setMetricsForm((prev) => ({
        ...prev,
        date: new Date().toISOString().split("T")[0],
        impressions: "",
        clicks: "",
        ctr: "",
        spend: "",
        likes: "",
        comments: "",
        shares: "",
        new_followers: "",
      }));
    } catch (e) {
      console.error("Error saving metrics:", e);
      toast.error("Failed to save metrics");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitAdMetrics = async (e) => {
    e.preventDefault();
    if (!adForm.client_id) {
      toast.error("Please select a client");
      return;
    }
    if (!adForm.ad_name.trim()) {
      toast.error("Please enter an ad name");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/ad-metrics`, {
        ...adForm,
        impressions: parseInt(adForm.impressions) || 0,
        clicks: parseInt(adForm.clicks) || 0,
        ctr: parseFloat(adForm.ctr) || 0,
        spend: parseFloat(adForm.spend) || 0,
        conversions: parseInt(adForm.conversions) || 0,
      });
      toast.success("Ad metrics saved successfully!");
      onSave();
      // Reset form but keep client and platform
      setAdForm((prev) => ({
        ...prev,
        ad_name: "",
        date: new Date().toISOString().split("T")[0],
        impressions: "",
        clicks: "",
        ctr: "",
        spend: "",
        conversions: "",
      }));
    } catch (e) {
      console.error("Error saving ad metrics:", e);
      toast.error("Failed to save ad metrics");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="manual-entry">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
          Manual Entry
        </h1>
        <p className="text-slate-500 mt-1">
          Enter metrics data manually for your clients
        </p>
      </div>

      <Tabs defaultValue="metrics" className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger
            value="metrics"
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-metrics"
          >
            Platform Metrics
          </TabsTrigger>
          <TabsTrigger
            value="ads"
            className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-ads"
          >
            Ad Metrics
          </TabsTrigger>
        </TabsList>

        {/* Platform Metrics Form */}
        <TabsContent value="metrics">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-100"
          >
            <form onSubmit={submitMetrics} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="client">Client *</Label>
                  <Select
                    value={metricsForm.client_id}
                    onValueChange={(v) => handleMetricsChange("client_id", v)}
                  >
                    <SelectTrigger className="mt-1" data-testid="metrics-client-select">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="platform">Platform</Label>
                  <Select
                    value={metricsForm.platform}
                    onValueChange={(v) => handleMetricsChange("platform", v)}
                  >
                    <SelectTrigger className="mt-1" data-testid="metrics-platform-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="tiktok">TikTok</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input
                    type="date"
                    value={metricsForm.date}
                    onChange={(e) => handleMetricsChange("date", e.target.value)}
                    className="mt-1"
                    data-testid="metrics-date-input"
                  />
                </div>
              </div>

              <div className="border-t border-slate-100 pt-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">
                  Performance Metrics
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="impressions">Impressions</Label>
                    <Input
                      type="number"
                      value={metricsForm.impressions}
                      onChange={(e) => handleMetricsChange("impressions", e.target.value)}
                      placeholder="0"
                      className="mt-1"
                      data-testid="metrics-impressions-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="clicks">Clicks</Label>
                    <Input
                      type="number"
                      value={metricsForm.clicks}
                      onChange={(e) => handleMetricsChange("clicks", e.target.value)}
                      placeholder="0"
                      className="mt-1"
                      data-testid="metrics-clicks-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ctr">CTR (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={metricsForm.ctr}
                      onChange={(e) => handleMetricsChange("ctr", e.target.value)}
                      placeholder="Auto-calculated"
                      className="mt-1 bg-slate-50"
                      data-testid="metrics-ctr-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="spend">Spend ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={metricsForm.spend}
                      onChange={(e) => handleMetricsChange("spend", e.target.value)}
                      placeholder="0.00"
                      className="mt-1"
                      data-testid="metrics-spend-input"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">
                  Engagement Metrics
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="likes">Likes</Label>
                    <Input
                      type="number"
                      value={metricsForm.likes}
                      onChange={(e) => handleMetricsChange("likes", e.target.value)}
                      placeholder="0"
                      className="mt-1"
                      data-testid="metrics-likes-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="comments">Comments</Label>
                    <Input
                      type="number"
                      value={metricsForm.comments}
                      onChange={(e) => handleMetricsChange("comments", e.target.value)}
                      placeholder="0"
                      className="mt-1"
                      data-testid="metrics-comments-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="shares">Shares</Label>
                    <Input
                      type="number"
                      value={metricsForm.shares}
                      onChange={(e) => handleMetricsChange("shares", e.target.value)}
                      placeholder="0"
                      className="mt-1"
                      data-testid="metrics-shares-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="followers">New Followers</Label>
                    <Input
                      type="number"
                      value={metricsForm.new_followers}
                      onChange={(e) => handleMetricsChange("new_followers", e.target.value)}
                      placeholder="0"
                      className="mt-1"
                      data-testid="metrics-followers-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 shadow-lg shadow-violet-500/20"
                  data-testid="save-metrics-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSubmitting ? "Saving..." : "Save Metrics"}
                </Button>
              </div>
            </form>
          </motion.div>
        </TabsContent>

        {/* Ad Metrics Form */}
        <TabsContent value="ads">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-100"
          >
            <form onSubmit={submitAdMetrics} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="ad-client">Client *</Label>
                  <Select
                    value={adForm.client_id}
                    onValueChange={(v) => handleAdChange("client_id", v)}
                  >
                    <SelectTrigger className="mt-1" data-testid="ad-client-select">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="ad-platform">Platform</Label>
                  <Select
                    value={adForm.platform}
                    onValueChange={(v) => handleAdChange("platform", v)}
                  >
                    <SelectTrigger className="mt-1" data-testid="ad-platform-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="tiktok">TikTok</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="ad-date">Date</Label>
                  <Input
                    type="date"
                    value={adForm.date}
                    onChange={(e) => handleAdChange("date", e.target.value)}
                    className="mt-1"
                    data-testid="ad-date-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="ad-name">Ad Name *</Label>
                  <Input
                    value={adForm.ad_name}
                    onChange={(e) => handleAdChange("ad_name", e.target.value)}
                    placeholder="e.g., Summer Sale Banner"
                    className="mt-1"
                    data-testid="ad-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="creative-type">Creative Type</Label>
                  <Select
                    value={adForm.creative_type}
                    onValueChange={(v) => handleAdChange("creative_type", v)}
                  >
                    <SelectTrigger className="mt-1" data-testid="ad-creative-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image">Image</SelectItem>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="carousel">Carousel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4">
                  Ad Performance
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <Label htmlFor="ad-impressions">Impressions</Label>
                    <Input
                      type="number"
                      value={adForm.impressions}
                      onChange={(e) => handleAdChange("impressions", e.target.value)}
                      placeholder="0"
                      className="mt-1"
                      data-testid="ad-impressions-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ad-clicks">Clicks</Label>
                    <Input
                      type="number"
                      value={adForm.clicks}
                      onChange={(e) => handleAdChange("clicks", e.target.value)}
                      placeholder="0"
                      className="mt-1"
                      data-testid="ad-clicks-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ad-ctr">CTR (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={adForm.ctr}
                      onChange={(e) => handleAdChange("ctr", e.target.value)}
                      placeholder="Auto"
                      className="mt-1 bg-slate-50"
                      data-testid="ad-ctr-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ad-spend">Spend ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={adForm.spend}
                      onChange={(e) => handleAdChange("spend", e.target.value)}
                      placeholder="0.00"
                      className="mt-1"
                      data-testid="ad-spend-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ad-conversions">Conversions</Label>
                    <Input
                      type="number"
                      value={adForm.conversions}
                      onChange={(e) => handleAdChange("conversions", e.target.value)}
                      placeholder="0"
                      className="mt-1"
                      data-testid="ad-conversions-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 shadow-lg shadow-violet-500/20"
                  data-testid="save-ad-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSubmitting ? "Saving..." : "Save Ad Metrics"}
                </Button>
              </div>
            </form>
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
